/**
 * Client-side mirror of the ALPHAPLAN argument validation in the backend
 * (integrations/alphaplan/schema_helpers.py), so the form flags a bad value before the mutation does.
 * Every validator returns the translation key (below `alphaplan.form.validation`) of the error, or null.
 */

export const MAX_BELEG_TYPE = 32767;

const ALLOWED_URL_PROTOCOLS = ['http:', 'https:'];
const FIELD_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;
const PEM_CERTIFICATE_HEADER = '-----BEGIN CERTIFICATE-----';
// printed on the purchase order, a deviation memo must never land there
const PRINTED_POSITION_FIELDS = ['PositionsText'];

export type AlphaplanValidationError =
    | 'baseUrlInvalid'
    | 'baseUrlCredentials'
    | 'fieldNameInvalid'
    | 'positionMemoFieldPrinted'
    | 'belegTypeInvalid'
    | 'caBundleInvalid';

export const validateBaseUrl = (value: string): AlphaplanValidationError | null => {
    const text = (value ?? '').trim();
    if (!text) return null;

    let parsed: URL;
    try {
        parsed = new URL(text);
    } catch {
        return 'baseUrlInvalid';
    }
    if (!ALLOWED_URL_PROTOCOLS.includes(parsed.protocol) || !parsed.hostname) return 'baseUrlInvalid';
    if (parsed.username || parsed.password) return 'baseUrlCredentials';
    return null;
};

export const validateFieldName = (value: string, allowsBlank = false): AlphaplanValidationError | null => {
    const text = (value ?? '').trim();
    if (!text && allowsBlank) return null;
    return FIELD_NAME_PATTERN.test(text) ? null : 'fieldNameInvalid';
};

export const validatePositionMemoField = (value: string): AlphaplanValidationError | null => {
    const fieldNameError = validateFieldName(value);
    if (fieldNameError) return fieldNameError;
    return PRINTED_POSITION_FIELDS.includes(value.trim()) ? 'positionMemoFieldPrinted' : null;
};

export const validateBelegType = (value: string): AlphaplanValidationError | null => {
    const text = (value ?? '').trim();
    if (!/^\d+$/.test(text)) return 'belegTypeInvalid';
    const parsed = Number(text);
    return parsed >= 1 && parsed <= MAX_BELEG_TYPE ? null : 'belegTypeInvalid';
};

export const validateCaBundle = (value: string): AlphaplanValidationError | null => {
    const text = (value ?? '').trim();
    if (!text) return null;
    return text.includes(PEM_CERTIFICATE_HEADER) ? null : 'caBundleInvalid';
};
